import type { Course } from '../types/academy'
import type { ForumChannel, ForumReply, ForumThread, Interest } from '../types/academyHub'

export const INTERESTS: Interest[] = [
  { id: 'data_analysis', label: 'Data Analysis', courses: ['data-driven-decision-making', 'research-frameworks-policy-investment'] },
  { id: 'ai_ml', label: 'AI and Machine Learning', courses: ['ai-readiness-five-dimensions', 'applied-ml-non-technical-leaders', 'responsible-ai-governance'] },
  { id: 'digital_governance', label: 'Digital Governance', courses: ['responsible-ai-governance', 'digital-government-delivery'] },
  { id: 'market_research', label: 'Market Research', courses: ['market-evidence-competitive-intelligence', 'data-driven-decision-making'] },
  { id: 'growth_strategy', label: 'Growth Strategy', courses: ['go-to-market-emerging-markets', 'digital-demand-generation-b2b-b2g'] },
  { id: 'public_sector_tech', label: 'Public Sector Technology', courses: ['digital-government-delivery', 'development-finance-intelligence'] },
]

export const GENERAL_CHANNELS: ForumChannel[] = [
  { slug: 'introductions', name: 'Introductions', description: 'Say who you are, where you work and what you want to learn.', type: 'general' },
  { slug: 'career-advice', name: 'Career advice', description: 'Moving into data, AI or governance roles.', type: 'general' },
  { slug: 'off-topic', name: 'Off-topic', description: 'Everything else, kept kind.', type: 'general' },
]

/** Every course gets its own channel the moment it is published. */
export function courseChannel(course: Pick<Course, 'slug' | 'title'>): ForumChannel {
  return {
    slug: course.slug,
    name: course.title,
    description: 'For learners enrolled in this course.',
    type: 'course',
    courseSlug: course.slug,
  }
}

const TEAM = { name: 'Cavalla Academy team', staff: true }

export const SEED_THREADS: ForumThread[] = [
  {
    id: 't-welcome',
    channel: 'introductions',
    title: 'Welcome to the Academy community',
    body:
      'This is the place to introduce yourself. Tell us your role, the country you work in, and the decision you most want better evidence for.\n\nCourse channels open automatically when you enrol, so questions about a specific lesson are best asked there.',
    author: TEAM,
    createdAt: '2026-10-01T08:00:00.000Z',
    upvotes: 24,
    pinned: true,
  },
  {
    id: 't-kigali-planner',
    channel: 'introductions',
    title: 'Planning officer in Kigali, starting with the delivery course',
    body:
      'Hello all. I coordinate district reporting for a ministry and we spend most of each quarter chasing late returns. Hoping Digital Government Delivery gives me a better structure for the stocktake meetings.',
    author: { name: 'Jean-Paul N.', detail: 'Planning officer, Rwanda' },
    createdAt: '2026-10-06T09:12:00.000Z',
    upvotes: 9,
  },
  {
    id: 't-move-into-data',
    channel: 'career-advice',
    title: 'Moving from M&E into a data analyst role',
    body:
      'I have five years in monitoring and evaluation, mostly Excel and KoboToolbox. Which course here is the best bridge into analyst work, and is SQL worth learning first?',
    author: { name: 'Grace O.', detail: 'M&E officer, Nigeria' },
    createdAt: '2026-10-08T14:40:00.000Z',
    upvotes: 14,
  },
  {
    id: 't-ai-readiness-welcome',
    channel: 'ai-readiness-five-dimensions',
    title: 'Start here: how to get the most from the self-assessment',
    body:
      'By Module 7 you will score your own organisation on all five dimensions. Keep notes as you go, one line per dimension per module, and the final assessment takes twenty minutes instead of two hours.',
    author: TEAM,
    createdAt: '2026-10-02T10:00:00.000Z',
    upvotes: 31,
    pinned: true,
  },
  {
    id: 't-governance-score',
    channel: 'ai-readiness-five-dimensions',
    title: 'Our Governance score came out at 1. Where do we start?',
    body:
      'Small NGO, 40 staff. Compute and Connectivity are fine because everything is cloud based, but nobody owns any of our data and there is no policy at all. Is a full AI policy the first step or something smaller?',
    author: { name: 'Wanjiru K.', detail: 'Operations lead, Kenya' },
    createdAt: '2026-10-09T11:05:00.000Z',
    upvotes: 18,
  },
  {
    id: 't-pivot-tables',
    channel: 'data-driven-decision-making',
    title: 'Pivot tables with messy district names',
    body:
      'Module 3 example works but on our own data the districts appear three or four times each because of spelling. Is there a quick way to fix this before pivoting?',
    author: { name: 'Kofi A.', detail: 'Analyst, Ghana' },
    createdAt: '2026-10-10T16:22:00.000Z',
    upvotes: 7,
  },
]

export const SEED_REPLIES: ForumReply[] = [
  {
    id: 'r-1',
    threadId: 't-move-into-data',
    body:
      'M&E is a strong starting point, you already think in indicators. Data-Driven Decision Making first, then Research Frameworks. SQL is worth it once you are working with data that no longer fits in a spreadsheet.',
    author: TEAM,
    createdAt: '2026-10-08T16:02:00.000Z',
    upvotes: 11,
  },
  {
    id: 'r-2',
    threadId: 't-move-into-data',
    parentId: 'r-1',
    body: 'Agree on SQL. I learned it on our DHIS2 exports and it changed how fast I could answer questions.',
    author: { name: 'Samuel T.', detail: 'Data officer, Ethiopia' },
    createdAt: '2026-10-09T07:45:00.000Z',
    upvotes: 4,
  },
  {
    id: 'r-3',
    threadId: 't-governance-score',
    body:
      'Smaller. Name one owner for the one dataset your first use case depends on, and write a one page acceptable use note for generative AI. Module 6 has a checklist that fits on a page.',
    author: TEAM,
    createdAt: '2026-10-09T13:30:00.000Z',
    upvotes: 15,
  },
  {
    id: 'r-4',
    threadId: 't-pivot-tables',
    body: 'Add a helper column with =PROPER(TRIM(A2)) and pivot on that. It fixes case and stray spaces in one go.',
    author: { name: 'Amina D.', detail: 'Statistician, Senegal' },
    createdAt: '2026-10-10T18:10:00.000Z',
    upvotes: 6,
  },
  {
    id: 'r-5',
    threadId: 't-kigali-planner',
    body: 'Welcome Jean-Paul. Module 4 on stocktakes is the one our planning unit found most useful.',
    author: { name: 'Fatou S.', detail: 'Delivery unit, The Gambia' },
    createdAt: '2026-10-06T12:00:00.000Z',
    upvotes: 3,
  },
]
